// A single task row in the Tasks <Table>. Renders as a <tr> matching the
// 5-column layout from TaskListHeader. Used at the `md` breakpoint and
// above; on narrower viewports `TaskTable` switches to `TaskCard` instead.
//
// `readOnly` (default off) is for the public shared views: the row is plain
// data -- no button role, no pointer, no click -- rather than a control that
// does nothing. `onTaskClicked={noop}` is the older recipe and still works, but
// it leaves a focusable "button" with no effect for a keyboard or AT user.
//
// PHOTOS RIDE A CONTINUATION ROW, the logbook lists' shape exactly: a second
// <tr> under this one carrying the task's thumbnail strip, paired to it by the
// `leg-row`/`leg-note-row` CSS (App.css). That CSS keys on the class names and
// nothing else, so it is reused rather than copied -- a second pair of classes
// would mean a second set of adjacent-sibling selectors to keep in step.
//
//   - NO PHOTOS, NO SECOND ROW. `leg-row` zeroes this row's bottom border on
//     the assumption something follows it; an always-present empty row would
//     make that lie under every task.
//   - `colSpan` is TASK_LIST_COLUMNS (+1 for `children`, the trailing <td>
//     nobody passes today). Declared beside the <th>s so it cannot drift.
//   - THE PAIR MUST AGREE. A row holding a control of any kind -- the assignee
//     link, or the strip's thumbnails -- drops `role="button"` but keeps its
//     `onClick`, and the continuation row takes the same answer, because the
//     CSS makes the two read and hover as one row.
//
// `rowActions` is a node that rides the SAME continuation row -- an Edit button,
// a Complete button, an "add photos" control. It exists because the shared task
// list must be able to grow controls WITHOUT the row becoming a button: a shared
// row still has nowhere to navigate, so `readOnly` keeps meaning exactly what it
// means (plain data) and the new capability lives in explicit controls beside
// the data rather than in a click on it.
//
// `photos`, `onOpenPhoto` and `rowActions` all default to absent, so every
// caller that passes none renders exactly the element tree it rendered before.

import { Fragment } from 'react';
import { StatusBadge } from '../../components';
import { shortDisplayDate } from '../../fleet-shared/Common.mjs';
import { useNavigate } from 'react-router-dom';
import { EntryPhotoStrip } from '../logbook/EntryPhotoStrip';
import { TASK_LIST_COLUMNS } from './TaskListHeader';
import {
  AssigneeCell,
  Stack,
  assigneeLinkUrl,
  displayPriorityForTaskLine,
  formatCounterInterval,
  formatInterval,
  navigateToTask,
} from './taskRowHelpers';

export const TaskLine = ({
  task,
  onTaskClicked,
  children,
  tasksListBucket,
  assigneeLinkFor,
  readOnly = false,
  photos,
  onOpenPhoto,
  photoLimit,
  rowActions,
}) => {
  const navigate = useNavigate();

  function taskClicked() {
    if (readOnly) {
      return;
    }
    if (onTaskClicked) {
      onTaskClicked(task);
      return;
    }
    navigateToTask(navigate, task, tasksListBucket);
  }

  // A row holding a control (the assignee link, or a photo thumbnail) drops
  // role="button" but keeps its onClick -- a button may not contain another
  // interactive element -- the same answer the logbook tables give their Trip
  // Name link.
  const assigneeUrl = assigneeLinkUrl(task, assigneeLinkFor);
  const withPhotos = Boolean(photos && photos.length > 0 && onOpenPhoto);
  // Either one puts a real control inside the pair, so either one is enough to
  // drop role="button" and to earn a continuation row.
  const withContinuation = withPhotos || Boolean(rowActions);
  const withControls = Boolean(assigneeUrl) || withContinuation;

  const rowClick = readOnly ? undefined : taskClicked;
  const rowRole = readOnly || withControls ? undefined : 'button';
  const rowStyle = readOnly ? undefined : { cursor: 'pointer' };

  return (
    <Fragment>
      <tr
        className={withContinuation ? 'leg-row' : undefined}
        onClick={rowClick}
        role={rowRole}
        style={rowStyle}
      >
        <td className="align-middle">
          <StatusBadge status={task.bucketName}/>
        </td>
        <td className="align-middle">
          <Stack items={[
            <span className="entered-data">{task.name}</span>,
            task.description,
            task.notes,
          ]} />
        </td>
        <td className="align-middle">
          <Stack items={[
            task.dueCounter,
            task.itemCounter ? task.itemCounter.name : '',
            formatCounterInterval(task),
          ]} />
        </td>
        <td className="align-middle">
          <Stack items={[
            shortDisplayDate(task.dueDate),
            formatInterval(task),
          ]} />
        </td>
        <td className="align-middle">
          <Stack items={[
            // An element is truthy, so guard on the name: an unassigned row must
            // still stack nothing here and keep priority on the primary line.
            task.assignedEmail ? <AssigneeCell task={task} url={assigneeUrl} /> : '',
            displayPriorityForTaskLine(task.priority),
          ]} />
        </td>
        {children}
      </tr>

      {withContinuation && (
        // Same role, same click, same cursor as the row above: the CSS pairs
        // them, so a reader hovering either half sees one row and must get one
        // behaviour.
        <tr className="leg-note-row" onClick={rowClick} role={rowRole} style={rowStyle}>
          <td colSpan={TASK_LIST_COLUMNS + (children ? 1 : 0)}>
            {withPhotos && (
              <EntryPhotoStrip
                photos={photos}
                onOpenPhoto={onOpenPhoto}
                // The same destination the row click has, so there is no second
                // navigation rule to keep in step.
                onOpenEntry={taskClicked}
                limit={photoLimit}
              />
            )}
            {rowActions}
          </td>
        </tr>
      )}
    </Fragment>
  );
};
